/**
 * AudioTrack - Displays audio clips with waveform visualization
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Volume2, VolumeX, Mic, Music, ChevronDown, ChevronRight } from 'lucide-react';
import { useWaveform } from '../hooks/useWaveform';
import { getEffectiveDuration } from '../utils/clipUtils';

// Audio clip component with real waveform
function AudioClipWithWaveform({ 
  clip, 
  clipWidth, 
  clipLeft, 
  isSelected, 
  isDragging, 
  isMuted,
  trackIndex,
  index,
  onSelect,
  onDragStart,
  onTrimStart,
  onDurationDetected,
  audioAssets,
  getAssetUrl
}) {
  // Look up the actual audio file URL from audioAssets
  const audioFileUrl = useMemo(() => {
    if (clip.asset_url) return clip.asset_url;
    if (!clip.asset_id || !audioAssets?.length) return null;
    const asset = audioAssets.find(a => a.id === clip.asset_id);
    return asset?.file_url || null;
  }, [clip.asset_url, clip.asset_id, audioAssets]);

  // Get real waveform from Web Audio API
  // Use a fixed sample count to ensure consistent caching across split clips
  // We'll slice this data based on trim points later
  const WAVEFORM_SAMPLES = 500; // Fixed samples for consistent cache
  const { waveformData, loading, duration: audioDuration } = useWaveform(
    audioFileUrl,
    WAVEFORM_SAMPLES
  );

  // Auto-correct clip duration if we detect it's wrong (e.g., was set to default 30s)
  useEffect(() => {
    if (audioDuration && audioDuration > 0 && onDurationDetected) {
      // If the clip's duration_ms differs significantly from actual audio duration, report it
      const difference = Math.abs(clip.duration_ms - audioDuration);
      // Only update if difference is more than 500ms and clip seems to have default duration
      if (difference > 500 && (clip.duration_ms === 30000 || clip.duration_ms === 5000)) {
        onDurationDetected(clip.id, audioDuration);
      }
    }
  }, [audioDuration, clip.id, clip.duration_ms, onDurationDetected]);

  // Calculate the effective clip duration on the timeline
  const clipDurationMs = getEffectiveDuration(clip);

  // Calculate actual display width and trim the waveform data
  const { displayWidth, trimmedWaveform } = useMemo(() => {
    if (!waveformData || waveformData.length === 0) {
      return { displayWidth: clipWidth, trimmedWaveform: null };
    }

    // audioDuration is the FULL original audio file duration from useWaveform
    // clip.duration_ms is also the original duration (doesn't change on split)
    const actualAudioDuration = audioDuration || clip.duration_ms;
    const trimStartMs = clip.trim_start_ms || 0;
    const trimEndMs = clip.trim_end_ms || 0;
    
    // The visible portion of the audio after trims
    const visibleAudioDuration = Math.max(0, actualAudioDuration - trimStartMs - trimEndMs);
    
    // clipDurationMs from getEffectiveDuration is the timeline duration
    // For split clips, the waveform should fill the entire clip width
    // because clipWidth is already calculated from the effective duration
    
    // Slice waveform to show only the trimmed portion
    const totalSamples = waveformData.length;
    if (actualAudioDuration > 0 && totalSamples > 0) {
      // Calculate which portion of the waveform to show based on trim points
      const startRatio = trimStartMs / actualAudioDuration;
      const endRatio = 1 - (trimEndMs / actualAudioDuration);
      
      const startIndex = Math.floor(startRatio * totalSamples);
      const endIndex = Math.ceil(endRatio * totalSamples);
      
      const slicedWaveform = waveformData.slice(
        Math.max(0, startIndex),
        Math.min(totalSamples, endIndex)
      );
      
      // The sliced waveform should fill the entire clip width
      // because the clip width is already calculated from the trimmed duration
      return {
        displayWidth: clipWidth,
        trimmedWaveform: slicedWaveform
      };
    }

    return { displayWidth: clipWidth, trimmedWaveform: waveformData };
  }, [waveformData, audioDuration, clip.duration_ms, clip.trim_start_ms, clip.trim_end_ms, clipDurationMs, clipWidth]);
  
  return (
    <div
      className={`absolute top-1 bottom-1 rounded select-none transition-all ${
        isDragging
          ? 'cursor-grabbing opacity-80 z-20'
          : 'cursor-grab'
      } ${
        isSelected
          ? 'ring-2 ring-white shadow-lg z-10'
          : 'hover:ring-1 hover:ring-zinc-500'
      } ${
        isMuted
          ? 'bg-zinc-700/60 opacity-50'
          : 'bg-emerald-600/70'
      }`}
      style={{
        left: clipLeft,
        width: Math.max(clipWidth, 4)
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(e);
      }}
      onMouseDown={onDragStart}
      data-testid={`audio-clip-${trackIndex}-${index}`}
    >
      {/* Waveform visualization */}
      {clipWidth > 20 && (
        <div className="absolute inset-0 overflow-hidden rounded flex items-start px-0.5">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-emerald-300/50 border-t-emerald-300 rounded-full animate-spin" />
            </div>
          ) : trimmedWaveform && trimmedWaveform.length > 0 ? (
            <div style={{ width: displayWidth, height: '100%', flexShrink: 0 }}>
              <svg 
                className="w-full h-full" 
                preserveAspectRatio="none"
                viewBox={`0 0 ${trimmedWaveform.length} 1`}
              >
                {trimmedWaveform.map((height, i) => (
                  <rect
                    key={i}
                    x={i}
                    y={(1 - height) / 2}
                    width={0.8}
                    height={height}
                    fill="currentColor"
                    className={isMuted ? 'text-zinc-400' : 'text-emerald-300'}
                    opacity={0.7}
                  />
                ))}
              </svg>
            </div>
          ) : null}
        </div>
      )}
      
      {/* Clip label */}
      {clipWidth > 50 && (
        <div className="absolute top-0.5 left-1.5 right-1.5">
          <span className="text-[9px] text-white font-medium truncate block drop-shadow-lg">
            {clip.asset_name}
          </span>
        </div>
      )}
      
      {/* Duration badge */}
      {clipWidth > 60 && (
        <div className="absolute bottom-0.5 right-1 text-[8px] text-emerald-200/70">
          {Math.round(clipDurationMs / 1000)}s
        </div>
      )}
      
      {/* Trim handles */}
      {isSelected && clipWidth > 20 && (
        <>
          <div 
            className="absolute left-0 top-0 bottom-0 w-2 bg-white/0 hover:bg-white/30 cursor-ew-resize rounded-l group z-30"
            onMouseDown={(e) => onTrimStart?.(e, 'start')}
          >
            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-white/50 rounded group-hover:bg-white" />
          </div>
          <div 
            className="absolute right-0 top-0 bottom-0 w-2 bg-white/0 hover:bg-white/30 cursor-ew-resize rounded-r group z-30"
            onMouseDown={(e) => onTrimStart?.(e, 'end')}
          >
            <div className="absolute right-0.5 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-white/50 rounded group-hover:bg-white" />
          </div>
        </>
      )}
    </div>
  );
}

export function AudioTrack({
  track,
  trackIndex,
  totalDuration,
  pxPerMs,
  totalWidth,
  labelWidth,
  playheadPosition,
  selectedClipIds,
  audioAssets,
  onSelectClip,
  onMoveClip,
  onTrimClip,
  onDropAudio,
  onUpdateTrack,
  onRenameTrack,
  onAudioDurationDetected,
  onClick,
  getAssetUrl,
  isCollapsed = false,
  onToggleCollapse
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(track.name);
  const trackRef = useRef(null);
  const renameInputRef = useRef(null);

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e) => {
    if (!dragState || !trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const deltaX = currentX - dragState.startX;
    const deltaMs = deltaX / pxPerMs;
    
    if (dragState.type === 'move') {
      const newStartMs = Math.max(0, dragState.originalValue + deltaMs);
      onMoveClip?.(dragState.clipId, 'audio', Math.round(newStartMs), track.id);
    } else if (dragState.type === 'trim-start') {
      const clip = track.clips?.find(c => c.id === dragState.clipId);
      if (clip) {
        const newTrimStart = Math.max(0, Math.min(
          dragState.originalTrimStart + deltaMs,
          clip.duration_ms - 100
        ));
        onTrimClip?.(dragState.clipId, 'audio', { trim_start_ms: Math.round(newTrimStart) }, track.id);
      }
    } else if (dragState.type === 'trim-end') {
      const clip = track.clips?.find(c => c.id === dragState.clipId);
      if (clip) {
        const newTrimEnd = Math.max(0, Math.min(
          dragState.originalTrimEnd - deltaMs,
          clip.duration_ms - (clip.trim_start_ms || 0) - 100
        ));
        onTrimClip?.(dragState.clipId, 'audio', { trim_end_ms: Math.round(newTrimEnd) }, track.id);
      }
    }
  }, [dragState, pxPerMs, track, onMoveClip, onTrimClip]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setDragState(null);
  }, []);

  // Add global mouse handlers when dragging
  useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp]);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'audio-asset') {
        const rect = trackRef.current.getBoundingClientRect();
        const dropX = e.clientX - rect.left;
        const startMs = Math.max(0, dropX / pxPerMs);
        onDropAudio?.(track.id, data.asset, startMs);
      }
    } catch (err) {
      console.error('Drop failed:', err);
    }
  }, [track.id, pxPerMs, onDropAudio]);

  // Start move drag
  const handleMoveStart = useCallback((e, clip) => {
    e.stopPropagation();
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    setDragState({
      type: 'move',
      clipId: clip.id,
      startX: e.clientX - rect.left,
      originalValue: clip.start_ms
    });
    onSelectClip?.(clip.id, 'audio', e.shiftKey || e.metaKey || e.ctrlKey);
  }, [onSelectClip]);

  // Start trim drag
  const handleTrimStart = useCallback((e, clip, edge) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    setDragState({
      type: edge === 'start' ? 'trim-start' : 'trim-end',
      clipId: clip.id,
      startX: e.clientX - rect.left,
      originalTrimStart: clip.trim_start_ms || 0,
      originalTrimEnd: clip.trim_end_ms || 0
    });
    onSelectClip?.(clip.id, 'audio', false);
  }, [onSelectClip]);

  // Toggle mute
  const handleToggleMute = useCallback((e) => {
    e.stopPropagation();
    onUpdateTrack?.(track.id, { muted: !track.muted });
  }, [track.id, track.muted, onUpdateTrack]);

  // Handle track click
  const handleTrackClick = useCallback((e) => {
    if (e.target === trackRef.current || e.target.classList.contains('track-background')) {
      onClick?.(e);
    }
  }, [onClick]);

  // Get track icon based on name - memoized to avoid recreating during render
  const TrackIconComponent = useMemo(() => {
    const name = track.name?.toLowerCase() || '';
    if (name.includes('music')) return Music;
    if (name.includes('voice') || name.includes('dialog')) return Mic;
    return Volume2;
  }, [track.name]);

  return (
    <div 
      className={`${isCollapsed ? 'h-8' : 'h-14'} border-b border-zinc-800/50 flex shrink-0 transition-all duration-200 ${
        isDragOver ? 'bg-emerald-900/20' : 'bg-zinc-900/30'
      }`}
      onClick={handleTrackClick}
    >
      {/* Track label - sticky on left */}
      <div 
        className="shrink-0 border-r border-zinc-800/50 flex items-center px-2 bg-zinc-900/50 gap-1 sticky left-0 z-10"
        style={{ width: labelWidth }}
      >
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleCollapse?.(); }}
          className="p-0.5 hover:bg-zinc-700 rounded text-zinc-500 hover:text-zinc-300"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        <button
          onClick={handleToggleMute}
          className={`p-1 rounded hover:bg-zinc-700 transition-colors ${
            track.muted ? 'text-red-400' : 'text-emerald-400'
          }`}
          title={track.muted ? 'Unmute' : 'Mute'}
        >
          {track.muted ? (
            <VolumeX className="w-3 h-3" />
          ) : (
            <TrackIconComponent className="w-3 h-3" />
          )}
        </button>
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => {
              setIsRenaming(false);
              if (renameValue.trim() && renameValue !== track.name) {
                onRenameTrack?.(track.id, renameValue.trim());
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.target.blur();
              } else if (e.key === 'Escape') {
                setRenameValue(track.name);
                setIsRenaming(false);
              }
            }}
            className="text-[10px] text-white bg-zinc-800 border border-zinc-600 rounded px-1 py-0.5 flex-1 outline-none"
            autoFocus
          />
        ) : (
          <span 
            className="text-[10px] text-zinc-500 truncate flex-1 cursor-pointer hover:text-zinc-300"
            onDoubleClick={() => {
              setRenameValue(track.name);
              setIsRenaming(true);
            }}
            title="Double-click to rename"
          >
            {track.name}
          </span>
        )}
      </div>
      
      {/* Clips area */}
      <div 
        ref={trackRef}
        className="relative flex-1 overflow-hidden track-background"
        style={{ width: totalWidth }}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
      >
        {!isCollapsed && track.clips?.map((clip, index) => {
          const isSelected = selectedClipIds?.includes(clip.id);
          const isDragging = dragState?.clipId === clip.id;
          const isMuted = !clip.settings?.active;
          
          const effectiveDuration = clip.duration_ms - (clip.trim_start_ms || 0) - (clip.trim_end_ms || 0);
          const clipWidth = effectiveDuration * pxPerMs;
          const clipLeft = clip.start_ms * pxPerMs;
          
          return (
            <AudioClipWithWaveform
              key={clip.id}
              clip={clip}
              clipWidth={clipWidth}
              clipLeft={clipLeft}
              isSelected={isSelected}
              isDragging={isDragging}
              isMuted={isMuted}
              trackIndex={trackIndex}
              index={index}
              audioAssets={audioAssets}
              onSelect={(e) => {
                if (!dragState) {
                  onSelectClip?.(clip.id, 'audio', e.shiftKey || e.metaKey || e.ctrlKey);
                }
              }}
              onDragStart={(e) => handleMoveStart(e, clip)}
              onTrimStart={(e, edge) => handleTrimStart(e, clip, edge)}
              onDurationDetected={onAudioDurationDetected}
              getAssetUrl={getAssetUrl}
            />
          );
        })}
        
        {/* Drop indicator */}
        {isDragOver && !isCollapsed && (
          <div className="absolute inset-0 border-2 border-dashed border-emerald-500 bg-emerald-500/10 pointer-events-none rounded" />
        )}
      </div>
    </div>
  );
}
